// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The top bar (M3-37, 08 §3): "the HCC logo and a back button".
//
// The back button is the part with a decision in it. A browser already has one, so a second one
// that does the same thing is clutter — and on a phone installed to the home screen there is no
// browser chrome at all, which is exactly where it stops being clutter. So it appears when there
// is somewhere to go back *to within the app*, and the front door never shows one.

import { Link, useNavigate } from 'react-router'
import { Icon } from './Icon'
import { ThemeToggle } from './ThemeToggle'
import type { ThemeController } from '../theme'
import { useLocalisation } from '../i18n/useLanguage'

export interface TopBarProps {
  readonly title: string
  /** False on the landing route: a back button on the front door goes nowhere useful. */
  readonly canGoBack: boolean
  readonly theme: ThemeController
}

export function TopBar({ title, canGoBack, theme }: TopBarProps): React.JSX.Element {
  const { t } = useLocalisation()
  const navigate = useNavigate()
  return (
    <header className="topbar">
      {canGoBack ? (
        <button
          type="button"
          className="button button--quiet"
          title={t('nav.back')}
          onClick={() => void navigate(-1)}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M15 5 L8 12 L15 19" />
          </svg>
          <span className="sr-only">{t('nav.back-label')}</span>
        </button>
      ) : (
        <Link to="/" className="topbar__logo" title={t('nav.home-title')}>
          <Icon name="play" filled size={22} />
          <span>HCC PINBALL</span>
        </Link>
      )}

      {/*
        **A paragraph, not the page's `h1`** (S3-04). This is a navigation label — "Arcade",
        "Settings", "Machine Builder" — and it was the only `h1` on the public pages, which made
        every one of them headed by its own tab's name instead of by what the page is. It was also
        a *second* `h1` on the account pages, which render their own: two top-level headings is a
        document with two subjects, and a screen reader announces both.

        The page owns its heading now. This keeps the class, so nothing about how it looks moves.
      */}
      <p className="topbar__title">{title}</p>

      <div className="topbar__links">
        <ThemeToggle theme={theme} />
      </div>
    </header>
  )
}
